/**
 * The explicit commands Ask Yelo understands before it looks for a customer
 * (pure, unit-tested).
 *
 * Every command is anchored to the START of the question and its keyword must
 * be a whole word: the old patterns matched anywhere, so a customer name that
 * merely contains "old" / "log" ("Harold Smith", "Arnold Khan") jumped to a
 * History search for the last word, and "hi" inside "this" / "which" answered
 * "Hello" (audit ADM-16). Arabic keywords are followed by a space, which is
 * their word boundary (JavaScript's \b does not see Arabic letters).
 */

import { contractNotFoundText, contractUncheckedText } from '../services/contractText';

// "history 123", "archive for Ali", "old search 5555", "سجل عن 123"
const HISTORY_RE = /^(?:history|archive|old|log|سجل|ارشيف|أرشيف|قديم)(?:\s+(?:search|for|عن))?\s+(.+)$/i;
// "find car 41287", "search fleet ...", "look for vehicle ..."
const FLEET_RE = /^(?:find|search|show|look for|get)\s+(?:car|vehicle|fleet|auto|سيارة|عربية|موتر|الموتر)\s+(.+)$/i;
// "find contract ...", "بحث عن عقد ..."
const CONTRACT_RE = /^(?:(?:find|search|show)\s+|بحث\s+(?:عن\s+)?|عن\s+)(?:contract|agreement|paper|عقد|العقد|اتفاقية)\s+(.+)$/i;
// "find staff ...", "search employee ..."
const STAFF_RE = /^(?:find|search|show)\s+(?:staff|employee|driver)\s+(.+)$/i;
const GREETING_RE = /\b(?:hello|hi)\b/i;

const termOf = (re, q) => {
    const m = String(q || '').trim().match(re);
    return m ? m[1].trim() : null;
};

/** The search term of an explicit History command, or null. */
export const historyTerm = (q) => termOf(HISTORY_RE, q);
/** The search term of an explicit fleet command, or null. */
export const fleetTerm = (q) => termOf(FLEET_RE, q);
/** The search term of an explicit contract command, or null. */
export const contractTerm = (q) => termOf(CONTRACT_RE, q);
/** The search term of an explicit staff command, or null. */
export const staffTerm = (q) => termOf(STAFF_RE, q);
/** "hello" / "hi" as a whole word (not inside "this", "which", "Hilton"). */
export const isGreeting = (q) => GREETING_RE.test(String(q || '').trim());

/**
 * A typed contract number (Ejar format) -> Ask Yelo's answer, checked against the stores FIRST.
 * `find(no)` resolves the EXACT number ({ row } from the contacts store or the contract records,
 * null when found nowhere; it throws when it could not check). A number that matches no contract
 * says so and offers nothing: a wrong number (one digit missing) used to offer a Customer profile
 * of a made-up "Open" contract (26 Sep 2026).
 */
export async function contractCommand(contractNo, { find, canEngine = false }) {
    const typed = String(contractNo || '').trim().toUpperCase();
    let hit;
    try {
        hit = await find(typed);
    } catch (e) {
        return { text: contractUncheckedText(typed), type: 'error', options: [] };
    }
    if (!hit || !hit.row) return { text: contractNotFoundText(typed), type: 'error', options: [] };
    const c = hit.row;
    const no = String(c.c || '').trim().toUpperCase();
    if (no !== typed) return { text: contractNotFoundText(typed), type: 'error', options: [] };   // never another contract
    const enc = encodeURIComponent(no);
    const airport = /airport|terminal/i.test(c.b || '');
    const options = [
        { label: 'Customer profile', seed: { contractNo: no, identity: c.i || '', name: c.n || '', phone: c.p || '', branch: c.b || '' } },
        { label: airport ? 'Open in Airport' : 'Open in Monthly management', action: airport ? `/airport-dashboard?search=${enc}` : `/contracts?search=${enc}` },
    ];
    if (canEngine) options.push({ label: 'Search in The Engine', action: `/the-engine?tab=customer&q=${enc}` });
    const status = String(c.s || '').trim();
    return { text: `📝 ${no} · ${c.n || 'Customer'}${c.b ? ` · ${c.b}` : ''}${status ? ` · ${status}` : ''}`, type: 'choice', options };
}
